import { useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from 'v2/configs/rq-setup'
import { executionService } from 'v2/shared/services/execution-service'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import type {
  CreateTemplatePayload,
  UpdateTemplatePayload,
  WhatsAppAccount,
} from 'v2/types/api/settings'

export const getWhatsAppQueryKeys = () => ({
  templates: ['whatsappTemplates'],
  templatesConfig: ['whatsappTemplatesConfig'],
})

export const useWhatsAppTemplatesQuery = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: getWhatsAppQueryKeys().templates,
    // The WhatsApp templates proxy is not on the embed allow-list.
    enabled: (options?.enabled ?? true) && !isEmbedMode(),
    queryFn: async () => {
      const response = await executionService.getWhatsAppTemplates({})
      return response.data
    },
  })

export const useWhatsAppTemplatesConfigQuery = <TSelect = WhatsAppAccount[]>(
  select?: (data: WhatsAppAccount[]) => TSelect,
  options?: { enabled?: boolean },
) =>
  useQuery<WhatsAppAccount[], unknown, TSelect>({
    queryKey: getWhatsAppQueryKeys().templatesConfig,
    enabled: options?.enabled,
    queryFn: async () => {
      const response = await executionService.getWhatsAppTemplatesConfig()
      return response.data
    },
    select,
  })

export const useCreateWhatsAppTemplate = () =>
  useMutation({
    mutationFn: async ({ wabaId, payload }: { wabaId: string; payload: CreateTemplatePayload }) => {
      const response = await executionService.createWhatsAppTemplate(wabaId, payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getWhatsAppQueryKeys().templates })
    },
  })

export const useUpdateWhatsAppTemplate = () =>
  useMutation({
    mutationFn: async ({
      wabaId,
      templateId,
      payload,
    }: {
      wabaId: string
      templateId: string
      payload: UpdateTemplatePayload
    }) => {
      const response = await executionService.updateWhatsAppTemplate(wabaId, templateId, payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getWhatsAppQueryKeys().templates })
    },
  })

export const useUploadWhatsAppMedia = () =>
  useMutation({
    mutationFn: async ({ wabaId, mediaUrl }: { wabaId: string; mediaUrl: string }) => {
      const response = await executionService.uploadWhatsAppMedia(wabaId, { media_url: mediaUrl })
      return response.data
    },
  })

export const useDeleteWhatsAppTemplate = () =>
  useMutation({
    mutationFn: async ({ wabaId, templateId }: { wabaId: string; templateId: string }) => {
      const response = await executionService.deleteWhatsAppTemplate(wabaId, templateId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getWhatsAppQueryKeys().templates })
    },
  })
